import React, { Fragment, useState, useContext } from "react";
import { HashLink as Link } from "react-router-hash-link";

import { PropTypes } from "prop-types";

import {
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Stack,
  Tooltip,
} from "@mui/material";

import LoadingPage from "../../../react_utils/LoadingPage";
import ClientLocationFormModal from "./ClientLocationFormModal";
import ClientConfigureFormModal from "./ClientConfigureFormModal";

import {
  string2Datetime,
  datetime2FormattedString,
} from "../../../shared/utils/utils";
import Table from "../../../react_utils/table/Table";
import TablePagination from "../../../react_utils/table/TablePagination";
import CharsInput from "../../../react_utils/fields/CharsInput";
import Icon from "../../../react_utils/Icon";
import ClientRequestHeatmapModal from "./ClientRequestHeatmapModal";
import LocationInfoModal from "../location/LocationInfoModal";
import ModalFooter from "../../../react_utils/modals/ModalFooter";
import CustomModal from "../../../react_utils/modals/CustomModal";
import { AuthContext } from "../../../shared/contexts/AuthContext";
import { MessagingContext } from "../../MessagingContext";
import useSshTunnel from "../../../shared/hooks/client/useSshTunnel";
import useClients from "../../../shared/hooks/client/useClients";

export default function Clients({ state }) {
  const { user, apiClient } = useContext(AuthContext);
  const { showMessage } = useContext(MessagingContext);

  // chosen client
  const [chosenClient, setChosenClient] = useState(null);

  // chosen device to delete date
  const [chosenDateClient, setChosenDateClient] = useState(null);

  // chosen device to show network stats
  const [chosenNetStatsClient, setChosenNetStatsClient] = useState(null);

  const { pagination, order, clients, data, search, filter, resetDate } =
    useClients(apiClient, showMessage, true, state);

  const { chosenSshClientIds, startTunnel } = useSshTunnel(
    apiClient,
    showMessage,
  );

  return (
    <Fragment>
      {!clients ? <LoadingPage /> : null}
      <Grid container justify="center">
        <Grid container justifyContent="flex-end">
          <Stack direction="row" spacing={2} alignItems={"center"}>
            <CharsInput label="Suche" onChange={(v) => search.setInput(v)} />
            <IconButton
              sx={{ p: 0 }}
              disableFocusRipple
              disableRipple
              style={{ backgroundColor: "transparent" }}
              onClick={() => data.update()}
            >
              <Icon
                icon="refresh"
                fontSize="small"
                sx={{ color: "primary.main", marginTop: "15px !important" }}
              />
            </IconButton>
          </Stack>
        </Grid>
        <Grid
          xs={12}
          sm={12}
          md={12}
          item
          justifyContent="center"
          alignItems="center"
        >
          {clients && (
            <Table
              columns={[
                {
                  name: "Geräte-ID",
                  key: "id",
                  sort: {
                    value: order.get("serial"),
                    setValue: (ascending) => order.set("serial", ascending),
                  },
                },
                { name: "Standort", key: "location" },
                {
                  name: "",
                  key: "location_comment",
                  getLink: () => null,
                  headerprops: { style: { width: 40 } },
                },
                {
                  name: "",
                  key: "location_link",
                  getLink: () => null,
                  headerprops: { style: { width: 40 } },
                },
                {
                  name: "Zuletzt online",
                  key: "timestamp",
                  sort: {
                    value: order.get("latest_query_timestamp"),
                    setValue: (ascending) =>
                      order.set("latest_query_timestamp", ascending),
                  },
                },
                {
                  name: "Online seit",
                  key: "online_since",
                  headerprops: { style: { width: 250 } },
                  onActive: (row) => {
                    return row.client.first_query_timestamp ? (
                      <IconButton
                        sx={{ p: 0 }}
                        disableFocusRipple
                        disableRipple
                        style={{ backgroundColor: "transparent" }}
                        onClick={() => setChosenDateClient(row.client)}
                      >
                        <Icon icon={"bin"} color="error" fontSize="small" />
                      </IconButton>
                    ) : null;
                  },
                  getLink: () => null,
                  sort: {
                    value: order.get("first_query_timestamp"),
                    setValue: (ascending) =>
                      order.set("first_query_timestamp", ascending),
                  },
                  filter: {
                    value: filter.beenOnline.value,
                    setValue: filter.beenOnline.setValue,
                    options: [
                      { value: true, label: "bereits online" },
                      { value: false, label: "noch nie online" },
                    ],
                  },
                },
                {
                  name: "",
                  key: "state",
                  filter: {
                    value: filter.online.value,
                    setValue: filter.online.setValue,
                    options: [
                      {
                        value: true,
                        label: (
                          <Chip
                            label="Online"
                            variant="outlined"
                            color="secondary"
                          />
                        ),
                      },
                      {
                        value: false,
                        label: (
                          <Chip
                            label="Offline"
                            variant="outlined"
                            color="error"
                          />
                        ),
                      },
                    ],
                  },
                },
                user?.is_superuser && {
                  name: "Version",
                  key: "git_hash",
                  sort: {
                    value: order.get("git_hash"),
                    setValue: (ascending) => order.set("git_hash", ascending),
                  },
                },
                user?.is_superuser && {
                  name: "",
                  key: "ssh",
                  headerprops: { style: { width: 50 } },
                  onActive: (row) =>
                    !chosenSshClientIds.includes(row.client.id) && (
                      <IconButton
                        sx={{ p: 0 }}
                        disableFocusRipple
                        disableRipple
                        style={{ backgroundColor: "transparent" }}
                        onClick={(e) => startTunnel(row.client.id, e)}
                      >
                        <Icon
                          icon={"ssh"}
                          sx={{ color: "secondary.main" }}
                          fontSize="small"
                        />
                      </IconButton>
                    ),
                  getLink: () => null,
                },
                {
                  name: "",
                  key: "netstats",
                  headerprops: { style: { width: 50 } },
                  onActive: (row) => (
                    <IconButton
                      sx={{ p: 0 }}
                      disableFocusRipple
                      disableRipple
                      style={{ backgroundColor: "transparent" }}
                      onClick={(e) => setChosenNetStatsClient(row.client)}
                    >
                      <Icon
                        icon={"wifi"}
                        sx={{ color: "secondary.main" }}
                        fontSize="small"
                      />
                    </IconButton>
                  ),
                  getLink: () => null,
                },
              ].filter((x) => x)}
              rows={clients.map((client) => ({
                id: client.serial,
                client,
                location_link: client.location && (
                  <Link
                    to={`/standort/${client.location}`}
                    style={{ textDecoration: "none", width: "100%" }}
                  >
                    <Icon
                      icon={"location"}
                      sx={{ color: "secondary.main" }}
                      fontSize="small"
                    />
                  </Link>
                ),
                location_comment: client.location && (
                  <LocationInfoModal
                    location={client._location}
                    resetParent={data.update}
                    modalProps={{
                      getOpenButton: (toggle) => (
                        <Tooltip
                          title={
                            client._location.notes || "Kommentar hinzufügen"
                          }
                        >
                          <div>
                            <IconButton
                              sx={{ p: 0 }}
                              disableFocusRipple
                              disableRipple
                              style={{ backgroundColor: "transparent" }}
                              onClick={toggle}
                            >
                              <Icon
                                icon={
                                  client._location.notes
                                    ? "comment"
                                    : "add-comment"
                                }
                                sx={{
                                  color: client._location.notes
                                    ? "secondary.main"
                                    : "tertiary.main",
                                }}
                                fontSize="small"
                              />
                            </IconButton>
                          </div>
                        </Tooltip>
                      ),
                    }}
                  />
                ),
                location:
                  client._location?.name || client._location?.address || "",
                timestamp: client.latest_query_timestamp
                  ? datetime2FormattedString(
                      string2Datetime(client.latest_query_timestamp),
                    )
                  : "",
                online_since: client.first_query_timestamp
                  ? datetime2FormattedString(
                      string2Datetime(client.first_query_timestamp),
                    )
                  : "",
                link: (row) => {
                  setChosenClient(client);
                },
                ssh: chosenSshClientIds.includes(client.id) && (
                  <CircularProgress
                    sx={{ color: "secondary.main" }}
                    size="20px"
                  />
                ),
                state: client.online ? (
                  <Chip label="Online" variant="outlined" color="secondary" />
                ) : (
                  <Chip label="Offline" variant="outlined" color="error" />
                ),
                git_hash: client.git_hash || "",
              }))}
              emptyTableText="Keine Steuergeräte"
            />
          )}
        </Grid>
        <Grid
          xs={12}
          sm={12}
          md={12}
          item
          display="flex"
          justifyContent="center"
          paddingTop={"10px"}
        >
          <TablePagination
            nPages={pagination.nPages}
            page={pagination.page}
            setPage={pagination.setPage}
          />
        </Grid>
      </Grid>
      <ClientLocationFormModal
        resetParent={data.update}
        client={chosenClient}
        modalProps={{
          isOpen: !!chosenClient && !chosenClient.location,
          setIsOpen: (isOpen) => {
            if (!isOpen) setChosenClient(null);
          },
        }}
      />
      <ClientConfigureFormModal
        resetParent={data.update}
        client={chosenClient}
        modalProps={{
          isOpen: !!chosenClient && !!chosenClient.location,
          setIsOpen: (isOpen) => {
            if (!isOpen) setChosenClient(null);
          },
        }}
      />
      <CustomModal
        title={"Soll das Datum wirklich zurückgesetzt werden?"}
        isOpen={!!chosenDateClient}
        setIsOpen={(isOpen) => {
          if (!isOpen) setChosenDateClient(null);
        }}
        getFooter={(toggle) => (
          <ModalFooter
            buttons={[
              {
                label: "Bestätigen",
                onClick: async () => resetDate(chosenDateClient.id, toggle),
              },
              { label: "Abrechen", onClick: async () => toggle },
            ]}
          />
        )}
      />
      <ClientRequestHeatmapModal
        client={chosenNetStatsClient}
        modalProps={{
          isOpen: !!chosenNetStatsClient,
          setIsOpen: (isOpen) => {
            if (!isOpen) setChosenNetStatsClient(null);
          },
        }}
      />
    </Fragment>
  );
}

Clients.propTypes = {
  state: PropTypes.number,
};
